import React, { useContext, useRef, useState } from 'react';
import { MdAdd } from 'react-icons/md';
import styled from 'styled-components';
import { GoalsModal } from '@/features/editor/widgets/shared/modals/goals-modal';
import { Tooltip } from '@/components/tooltip';
import { VSpace } from '@/components/spacing';
import {
  getCustomMetricsMap,
  MAX_SELECTED_GOALS,
} from '@/features/editor/widgets/shared/modals/use-goals-modal';
import { formatCustomGoalName } from '@/features/shared/labels-strip';
import { formatEnum } from '@/utils/types';
import { Flexbox } from '@/components/flex';
import { InlineVariantsLayoutHeaderText } from '@/features/editor/widgets/shared/inline-variants-layout/default-header';
import { EditorContext } from '@/features/editor/context/editor-context';
import { getAllowedPrimaryGoals } from '@/webapi/models';
import { DeviceType } from '@/utils/definitions';

export function InlineVariantsLayoutGoals() {
  const {
    experienceState: { currentExperience, setPrimaryGoal },
    devicePreview: {
      editorState: { device },
    },
    resources: { customMetrics },
  } = useContext(EditorContext);
  const fromRef = useRef(null);
  const [isGoalsModalVisible, setIsGoalsModalVisible] = useState(false);

  const onGoalsClick = () => {
    setIsGoalsModalVisible(true);
  };

  const customMetricsMap = getCustomMetricsMap(customMetrics);

  return (
    <>
      <GoalsModal
        fromRef={fromRef}
        isVisible={isGoalsModalVisible}
        setIsVisible={setIsGoalsModalVisible}
      />
      <Flexbox>
        <InlineVariantsLayoutHeaderText device={device}>
          Set Goals
          <Tooltip
            text="Choose which metrics you want to see in A/B validation reports"
            title="Set Goals"
          />
        </InlineVariantsLayoutHeaderText>
        <VSpace value={2} />
        <GoalsWrapper ref={fromRef} onClick={onGoalsClick}>
          {currentExperience.goals.length < MAX_SELECTED_GOALS && (
            <GoalChipAddMore>
              <MdAdd size={20} />
            </GoalChipAddMore>
          )}
          {currentExperience.goals.map((item) => (
            <GoalChip key={item} device={device} focused>
              {formatCustomGoalName(item, customMetricsMap) ||
                formatEnum(item, true)}
            </GoalChip>
          ))}
        </GoalsWrapper>
      </Flexbox>
      {currentExperience.goals.length > 1 && (
        <Flexbox>
          <VSpace value={2} />
          <InlineVariantsLayoutHeaderText device={device}>
            Set Primary Goal
            <Tooltip
              text="Choose which metric you want to emphasize in A/B validation reports"
              title="Set Primary Goal"
            />
          </InlineVariantsLayoutHeaderText>
          <VSpace value={2} />
          <GoalsWrapper>
            {getAllowedPrimaryGoals(currentExperience.goals)?.map((item) => (
              <PrimaryGoalChip
                key={item}
                device={device}
                focused={item === currentExperience?.primaryGoal}
                onClick={() => setPrimaryGoal(item)}
              >
                {formatCustomGoalName(item, customMetricsMap) ||
                  formatEnum(item, true)}
              </PrimaryGoalChip>
            ))}
          </GoalsWrapper>
        </Flexbox>
      )}
    </>
  );
}

type P = { device: DeviceType; focused?: boolean };

export const GoalsWrapper = styled.ul`
  && {
    margin: 0;
    width: 100%;
    padding: 0;

    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;

    max-width: 56.8rem;
    list-style: none;
    overflow-x: scroll;
    overflow-y: hidden;
    white-space: nowrap;

    && > *:not(:last-child) {
      margin-right: 1.5rem;
    }
  }
`;

export const GoalChip = styled.li`
  && {
    cursor: pointer;
    width: auto;
    opacity: ${({ focused }: P) => (focused ? `1` : `0.5`)};
    padding: 0.8rem 1.2rem;
    border: 1.5px solid #f2f4f6;
    border-radius: 1.8rem;
    user-select: none;
    font-size: 1.2rem;
    font-weight: 600;
    font-family: Inter, serif;
    color: #58606c;
    background: #f2f4f6;
    transition: opacity 0.2s ease-out;

    :hover {
      opacity: 0.8;
    }

    :active {
      opacity: 0.6;
    }
  }
`;

export const PrimaryGoalChip = styled(GoalChip)`
  && {
    border: 1.5px solid ${({ focused }: P) => (focused ? `#0086FF` : `#f2f4f6`)};
    color: ${({ focused }: P) => (focused ? `#0086FF` : `#58606c`)};
  }
`;

export const GoalChipAddMore = styled(GoalChip)`
  && {
    padding: 0;
    opacity: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 3.3rem;
    min-width: 3.3rem;
    background: #e3e9ee;
    color: #adb6be;
    border-radius: 50%;
  }
`;
